import React from 'react';
import { motion } from 'framer-motion';

export default function AnimatedNeoSymbol({ 
  size = 120, 
  showOrbits = true,
  orbitingElements = [] 
}) {
  return (
    <div className="relative flex items-center justify-center" style={{ width: size * 3, height: size * 3 }}>
      {/* Donut-shaped ring using SVG - positioned northwest */}
      <motion.div
        className="absolute"
        style={{
          width: size,
          height: size,
          left: 'calc(50% - 40px)',
          top: 'calc(50% - 40px)',
          transform: 'translate(-50%, -50%)'
        }}
        animate={{
          scale: [1, 1.05, 1],
          opacity: [0.8, 1, 0.8]
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          style={{ filter: 'drop-shadow(0 0 30px rgba(0, 194, 194, 0.3))' }}
        >
          <defs>
            <linearGradient id="donutGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#0A1A2F" />
              <stop offset="100%" stopColor="#00C2C2" />
            </linearGradient>
            <radialGradient id="glowGradient">
              <stop offset="50%" stopColor="transparent" />
              <stop offset="70%" stopColor="rgba(0,194,194,0.2)" />
              <stop offset="90%" stopColor="transparent" />
            </radialGradient>
          </defs>
          
          {/* Outer circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={(size * 0.35) - (size * 0.075)}
            fill="none"
            stroke="url(#donutGradient)"
            strokeWidth={size * 0.15}
          />
          
          {/* Glow effect */}
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={size * 0.4}
            fill="url(#glowGradient)"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </svg>
      </motion.div>

      {/* Orbiting elements - kept in center */}
      {showOrbits && orbitingElements.length > 0 && (
        <div className="absolute" style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
          {orbitingElements.map((element, index) => {
            const angle = (360 / orbitingElements.length) * index;
            
            // Determine radius based on angle - right side (315° to 45°) is closer
            const normalizedAngle = angle % 360;
            const isRightSide = normalizedAngle >= 315 || normalizedAngle <= 45;
            const baseRadius = size * 1.05; // Brought closer overall
            const radius = isRightSide ? baseRadius * 0.75 : baseRadius; // Right side 25% closer
            
            const x = Math.cos((angle - 90) * Math.PI / 180) * radius;
            const y = Math.sin((angle - 90) * Math.PI / 180) * radius;
            
            // Special positioning for "persona activity"
            const isPersonaActivity = element.toLowerCase() === "persona activity";
            const adjustedX = isPersonaActivity ? x - 90 : x;
            
            return (
              <motion.div
                key={index}
                className="absolute"
                style={{
                  left: adjustedX,
                  top: y,
                  transform: 'translate(-50%, -50%)'
                }}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.1, zIndex: 10 }}
              >
                <motion.div
                  className="bg-teal-500/20 text-teal-300 text-xs md:text-sm px-2 py-1 md:px-3 md:py-1.5 rounded-full border border-teal-400/30 backdrop-blur-sm whitespace-nowrap cursor-default hover:bg-teal-500/30 transition-all duration-300 shadow-lg"
                  animate={{
                    opacity: [0.4, 1, 0.4],
                    scale: [0.95, 1.05, 0.95]
                  }}
                  transition={{
                    duration: 3 + index * 0.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: index * 0.3
                  }}
                >
                  {element}
                </motion.div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Default Orbiting dots */}
      {showOrbits && orbitingElements.length === 0 && (
        <>
          {[0, 60, 120, 180, 240, 300].map((angle, index) => {
            const orbitRadius = size * 0.6;
            return (
              <motion.div
                key={index}
                className="absolute w-2 h-2 bg-teal-400 rounded-full opacity-60"
                style={{
                  left: '50%',
                  top: '50%',
                  marginLeft: -4,
                  marginTop: -4
                }}
                animate={{
                  x: [
                    Math.cos((angle - 90) * Math.PI / 180) * orbitRadius,
                    Math.cos((angle + 270) * Math.PI / 180) * orbitRadius
                  ],
                  y: [
                    Math.sin((angle - 90) * Math.PI / 180) * orbitRadius,
                    Math.sin((angle + 270) * Math.PI / 180) * orbitRadius
                  ]
                }}
                transition={{
                  duration: 8 + index * 1.5,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            );
          })}
        </>
      )}
    </div>
  );
}